import { Add } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import { useExplorerStore } from "../../../data/ExplorerStore";
import DefaultQueryGroupFilters from "./DefaultQueryGroupFilters";
import DimensionSelect from "./DimensionSelect";
import { FeatureFlags } from "../../../util/featureFlags";

interface Props {
  metricGroupIndex: number;
}

export default function QueryForm({ metricGroupIndex }: Props) {
  const isQueryGroup = metricGroupIndex === 0;
  const metricGroup = useExplorerStore(
    ({ metricGroups }) => metricGroups[metricGroupIndex]
  );
  const isLoading = useExplorerStore(
    ({ filters }) => !filters
  );
  const metrics = useExplorerStore(({ metrics }) => metrics);
  const addDimension = useExplorerStore(({ addDimension }) => addDimension);
  const setDimensionRatio = useExplorerStore(
    ({ setDimensionRatio }) => setDimensionRatio
  );
  const updateMetricGroup = useExplorerStore(
    ({ updateMetricGroup }) => updateMetricGroup
  );

  const hasPrimaryDimensions =
    FeatureFlags.dimensionGroups &&
    isQueryGroup &&
    metricGroup.dimensions.filter((d) => d.name).length >= 2 &&
    metricGroup.dimensions.some((d) => d.primary);

  return (
    <>
      <Box mb={1.5}>
        <Typography mb={0.5} fontSize={15} fontWeight={600}>
          Breakdowns
        </Typography>
        {isLoading ? (
          <Typography>Loading filters...</Typography>
        ) : (
          <>
            {metricGroup.dimensions.map((d, i) => (
              <DimensionSelect
                metricGroupIndex={metricGroupIndex}
                index={i}
                key={`queryBreakdown:${i}`}
                dimensionType="dimensions"
                showPrimaryToggle={
                  FeatureFlags.dimensionGroups &&
                  isQueryGroup &&
                  metricGroup.dimensions.filter((d) => d.name).length >= 2
                }
              />
            ))}
            <Button
              startIcon={<Add />}
              sx={{
                mt: 1.5,
                textTransform: "none",
                fontSize: "0.8rem",
                color: "rgba(0, 0, 0, 0.6)",
                borderColor: "#bdbdbd",
                "&:hover": { backgroundColor: "#eeeeee" },
              }}
              onClick={() => addDimension(metricGroupIndex, "dimensions")}
              variant="outlined"
              size="small"
            >
              Add Breakdown
            </Button>
            {hasPrimaryDimensions && (
              <Box
                mt={1.5}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1,
                  backgroundColor: "grey.50",
                }}
              >
                <Typography
                  fontSize="0.75rem"
                  textTransform="uppercase"
                  fontWeight={600}
                  color="rgba(0,0,0,0.6)"
                  mb={0.75}
                >
                  Ratio Metric
                </Typography>
                <Autocomplete
                  size="small"
                  options={metrics}
                  getOptionLabel={(o) => (typeof o === "string" ? o : o)}
                  value={metricGroup.dimensionRatio?.value ?? null}
                  onChange={(_e, value) =>
                    setDimensionRatio(
                      metricGroupIndex,
                      value ? { value, label: value } : null,
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="% of metric (e.g. Poss)"
                      size="small"
                    />
                  )}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!metricGroup.includeOverall}
                      onChange={(e) =>
                        updateMetricGroup(metricGroupIndex, {
                          ...metricGroup,
                          includeOverall: e.target.checked,
                        })
                      }
                      sx={{ py: 0, "& .MuiSvgIcon-root": { fontSize: "1rem" } }}
                    />
                  }
                  label="Include overall rows"
                  sx={{
                    mt: 0.75,
                    ml: 0,
                    "& .MuiTypography-root": { fontSize: "0.8rem", color: "rgba(0,0,0,0.6)" },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box mb={1.5}>
        <Typography mb={0.5} fontSize={15} fontWeight={600}>
          Filters
        </Typography>
        {isQueryGroup && (
          <>
            <DefaultQueryGroupFilters />
            <Divider sx={{ my: 1.5 }} />
          </>
        )}
        {isLoading ? (
          <Typography>Loading filters...</Typography>
        ) : (
          <>
            {metricGroup.filters.map(
              (filter, i) =>
                (!isQueryGroup || i > 2) && (
                  <DimensionSelect
                    metricGroupIndex={metricGroupIndex}
                    index={i}
                    key={`queryFilter:${i}`}
                    dimensionType="filters"
                  />
                )
            )}
            <Box>
              <Button
                startIcon={<Add />}
                sx={{
                  mt: 1.5,
                  textTransform: "none",
                  fontSize: "0.8rem",
                  color: "rgba(0, 0, 0, 0.6)",
                  borderColor: "#bdbdbd",
                  "&:hover": { backgroundColor: "#eeeeee" },
                }}
                onClick={() => addDimension(metricGroupIndex, "filters")}
                variant="outlined"
                size="small"
              >
                Add Filter
              </Button>
            </Box>
          </>
        )}
      </Box>
    </>
  );
}
